export const ADVISOR_BUYERS = Object.freeze([
  {
    id: "BY-1001",
    name: "Horizon Capital",
    location: "Singapore",
    desiredSector: "Technology, Software & Digital Services",
    desiredSubsector: "SaaS / Enterprise Software",
    budgetMin: 100000000000,
    budgetMax: 400000000000,
    liveCaseId: "AR-083402",
    documents: ["Acquisition mandate.pdf", "Proof of funds.pdf", "Investment committee profile.pdf"],
    updatedAt: "2026-08-29T09:15:00+07:00",
  },
  {
    id: "BY-1002",
    name: "Red River Partners",
    location: "Vietnam",
    desiredSector: "Technology, Software & Digital Services",
    desiredSubsector: "Cybersecurity",
    budgetMin: 150000000000,
    budgetMax: 350000000000,
    liveCaseId: "AR-901218",
    documents: ["Buyer profile.pdf", "Proof of funds.pdf"],
    updatedAt: "2026-08-28T16:20:00+07:00",
  },
  {
    id: "BY-1003",
    name: "Annam Consumer Partners",
    location: "Vietnam",
    desiredSector: "Food & Beverage Manufacturing",
    desiredSubsector: "Packaged Foods",
    budgetMin: 100000000000,
    budgetMax: 280000000000,
    liveCaseId: "AR-661476",
    documents: ["Acquisition criteria.pdf", "Funding confirmation.pdf"],
    updatedAt: "2026-08-27T11:45:00+07:00",
  },
  {
    id: "BY-1004",
    name: "Lotus Industrial Holdings",
    location: "Thailand",
    desiredSector: "Manufacturing",
    desiredSubsector: "Industrial Automation",
    budgetMin: 250000000000,
    budgetMax: 500000000000,
    liveCaseId: "AR-547802",
    documents: ["Strategic acquisition mandate.pdf"],
    updatedAt: "2026-08-26T14:05:00+07:00",
  },
]);

export const ADVISOR_MEETINGS = Object.freeze([
  { id: "MTG-001", partyType: "seller", partyId: "AR-083402", partyName: "CloudLedger Systems", date: "2026-08-29", start: "09:00", end: "09:45", requestedBy: "Minh Nguyen", status: "pending" },
  { id: "MTG-002", partyType: "buyer", partyId: "BY-1001", partyName: "Horizon Capital", date: "2026-08-29", start: "11:30", end: "12:00", requestedBy: "Alex Nguyen", status: "accepted" },
  { id: "MTG-003", partyType: "seller", partyId: "AR-661476", partyName: "Mekong Harvest Foods", date: "2026-08-30", start: "14:00", end: "14:45", requestedBy: "Lan Pham", status: "pending" },
  { id: "MTG-004", partyType: "buyer", partyId: "BY-1002", partyName: "Red River Partners", date: "2026-09-01", start: "10:00", end: "10:30", requestedBy: "Daniel Tran", status: "pending" },
  { id: "MTG-005", partyType: "seller", partyId: "AR-334150", partyName: "Northstar Digital", date: "2026-09-03", start: "15:00", end: "16:00", requestedBy: "Quang Vo", status: "declined" },
]);

export const ADVISOR_LIVE_DEALS = Object.freeze([
  { id: "LIVE-001", sellerId: "AR-083402", buyerId: "BY-1001", updatedAt: "2026-08-29T10:20:00+07:00" },
  { id: "LIVE-002", sellerId: "AR-901218", buyerId: "BY-1002", updatedAt: "2026-08-28T13:10:00+07:00" },
  { id: "LIVE-003", sellerId: "AR-661476", buyerId: "BY-1003", updatedAt: "2026-08-27T15:40:00+07:00" },
]);

const MARKET_STATE_KEY = "aragon.advisor.market-state.v1";
const MEETING_STATE_KEY = "aragon.advisor.meeting-state.v1";
const FINANCIAL_STATE_KEY = "aragon.advisor.financial-state.v1";
const NDA_STATE_KEY = "aragon.advisor.nda-offers.v1";
const LIVE_DEAL_STATE_KEY = "aragon.advisor.live-deals.v1";

const DEFAULT_MARKET_STATE = Object.freeze({
  "AR-083402": "approved",
  "AR-901218": "approved",
  "AR-334150": "pending",
  "AR-661476": "approved",
  "AR-547802": "pending",
});

function canUseSessionStorage() {
  return typeof window !== "undefined" && Boolean(window.sessionStorage);
}

function readJson(key, fallback) {
  if (!canUseSessionStorage()) return fallback;
  const raw = window.sessionStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (canUseSessionStorage()) window.sessionStorage.setItem(key, JSON.stringify(value));
}

export function getAdvisorBuyerById(buyerId) {
  return ADVISOR_BUYERS.find((buyer) => buyer.id === buyerId) || null;
}

export function readMarketState() {
  return { ...DEFAULT_MARKET_STATE, ...readJson(MARKET_STATE_KEY, {}) };
}

export function getSellerMarketStatus(caseId) {
  return readMarketState()[caseId] || "pending";
}

export function setSellerMarketStatus(caseId, status) {
  const next = { ...readMarketState(), [caseId]: status };
  writeJson(MARKET_STATE_KEY, next);
  return next;
}

export function readAdvisorMeetings() {
  const saved = readJson(MEETING_STATE_KEY, {});
  return ADVISOR_MEETINGS.map((meeting) => ({ ...meeting, ...(saved[meeting.id] || {}) }));
}

export function updateAdvisorMeetingStatus(meetingId, status) {
  const current = readJson(MEETING_STATE_KEY, {});
  const next = { ...current, [meetingId]: { ...(current[meetingId] || {}), status } };
  writeJson(MEETING_STATE_KEY, next);
  return readAdvisorMeetings();
}

function currentFinancialPeriod(sellerOrFinancials) {
  const seller = sellerOrFinancials?.financials ? sellerOrFinancials : null;
  const financials = seller?.financials || sellerOrFinancials || {};
  const sourceDocument = seller?.documents?.find((document) => /financial|statement|report/i.test(String(document))) || "Current financial report";
  const sourceYear = String(sourceDocument).match(/(?:FY)?(20\d{2})/i)?.[1];
  const nonEstimateYears = (financials.years || []).map(String).filter((year) => !/E$/i.test(year));
  const year = sourceYear || nonEstimateYears[nonEstimateYears.length - 1] || String(new Date().getFullYear());
  const revenue = seller ? Number(seller.revenue) / 1_000_000_000 : Number(financials.revenue?.[Math.max(0, nonEstimateYears.length - 1)]);
  const ebitda = seller ? Number(seller.ebitda) / 1_000_000_000 : Number(financials.ebitda?.[Math.max(0, nonEstimateYears.length - 1)]);
  return { year, revenue, ebitda, sourceDocument };
}

export function readSellerFinancialHistory(caseId, sellerOrFinancials) {
  const stored = readJson(FINANCIAL_STATE_KEY, {});
  const period = currentFinancialPeriod(sellerOrFinancials);
  const savedProfit = stored[caseId]?.netProfit?.[period.year];
  return [{
    year: period.year,
    revenue: Number.isFinite(period.revenue) ? period.revenue : null,
    ebitda: Number.isFinite(period.ebitda) ? period.ebitda : null,
    netProfit: savedProfit === null || savedProfit === undefined ? null : Number(savedProfit),
    revenueSource: "Extracted",
    ebitdaSource: "Extracted",
    netProfitSource: "Advisor entered",
    sourceDocument: period.sourceDocument,
  }];
}

export function saveSellerNetProfit(caseId, year, value) {
  const current = readJson(FINANCIAL_STATE_KEY, {});
  const currentCase = current[caseId] || { netProfit: {} };
  const numeric = value === "" || value === null || value === undefined ? null : Number(value);
  const next = {
    ...current,
    [caseId]: {
      ...currentCase,
      netProfit: {
        ...(currentCase.netProfit || {}),
        [year]: Number.isFinite(numeric) ? numeric : null,
      },
    },
  };
  writeJson(FINANCIAL_STATE_KEY, next);
  return next;
}

export function readAdvisorLiveDeals() {
  const created = readJson(LIVE_DEAL_STATE_KEY, []);
  const createdDeals = Array.isArray(created) ? created : [];
  const seen = new Set();
  return [...createdDeals, ...ADVISOR_LIVE_DEALS]
    .filter((deal) => {
      const key = `${deal.sellerId}::${deal.buyerId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
}

export function createAdvisorLiveDeal(sellerId, buyerId) {
  const existing = readAdvisorLiveDeals().find((deal) => deal.sellerId === sellerId && deal.buyerId === buyerId);
  if (existing) return { deal: existing, created: false };
  const deal = {
    id: `LIVE-${Date.now()}`,
    sellerId,
    buyerId,
    updatedAt: new Date().toISOString(),
  };
  const created = readJson(LIVE_DEAL_STATE_KEY, []);
  const next = [deal, ...(Array.isArray(created) ? created : [])];
  writeJson(LIVE_DEAL_STATE_KEY, next);
  return { deal, created: true };
}

export function readAdvisorNdaOffers() {
  const stored = readJson(NDA_STATE_KEY, []);
  return Array.isArray(stored) ? stored : [];
}

export function offerNdaToParty({ partyType, partyId, partyName, caseId }) {
  const current = readAdvisorNdaOffers();
  const existing = current.find((agreement) => agreement.partyType === partyType && agreement.partyId === partyId && agreement.status !== "signed");
  if (existing) return { agreements: current, agreement: existing, created: false };
  const agreement = {
    id: `ADV-NDA-${partyType.toUpperCase()}-${partyId}-${Date.now()}`,
    partyType,
    partyId,
    partyName,
    caseId: caseId || null,
    offeredAt: new Date().toISOString(),
    status: "pending",
    signer: null,
    signedAt: null,
  };
  const next = [agreement, ...current];
  writeJson(NDA_STATE_KEY, next);
  return { agreements: next, agreement, created: true };
}

export function readAdvisorBuyerNdas() {
  return readAdvisorNdaOffers()
    .filter((agreement) => agreement.partyType === "buyer" && agreement.caseId)
    .map((agreement) => ({
      id: agreement.id,
      caseId: agreement.caseId,
      offeredAt: agreement.offeredAt,
      status: agreement.status,
      signedAt: agreement.signedAt,
      signer: agreement.signer,
      advisorOffer: true,
    }));
}

export function recordAdvisorNdaSignature(agreementId, signer, signedAt = new Date().toISOString()) {
  const current = readAdvisorNdaOffers();
  const next = current.map((agreement) => agreement.id === agreementId
    ? { ...agreement, status: "signed", signer, signedAt }
    : agreement);
  writeJson(NDA_STATE_KEY, next);
  return next;
}

export function getAdvisorNdaForParty(partyType, partyId) {
  return readAdvisorNdaOffers()
    .filter((agreement) => agreement.partyType === partyType && agreement.partyId === partyId)
    .sort((a, b) => new Date(b.offeredAt) - new Date(a.offeredAt))[0] || null;
}
